import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  createInvoiceCarrier,
  getInvoiceCarrier,
  listInvoiceCarrier,
  removeInvoiceCarrier,
  updateInvoiceCarrier,
} from './invoiceCarrier.service';
import {
  InvoiceCarrierListRequest,
  InvoiceCarrierRequest,
  InvoiceCarrierUpdateRequest,
} from './invoiceCarrier.types';
import notificationError from '@/utils/functions/notificationError';

export const INVOICE_CARRIER_QUERY_KEY = 'invoice/carrier';

export function useInvoiceCarriers(
  params?: InvoiceCarrierListRequest,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ['invoiceCarrier', params],
    queryFn: ({ pageParam }) =>
      listInvoiceCarrier({ rows: 10, ...params, nextToken: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextToken,
    onError(error) {
      notificationError(error);
    },
    enabled,
  });
}

export function useCreateInvoiceCarrier() {
  const intl = useIntl().formatMessage;
  const query = useQueryClient();

  const mutation = useMutation(
    (obj: InvoiceCarrierRequest) => createInvoiceCarrier(obj),
    {
      onSuccess() {
        query.invalidateQueries(['invoiceCarrier']);
        notification.success({
          message: intl({
            id: 'invoiceTransport.notification.create.success.title',
          }),
          description: intl({
            id: 'invoiceTransport.notification.create.success.description',
          }),
        });
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}

export function useInvoiceCarrier(id?: number) {
  return useQuery(
    [INVOICE_CARRIER_QUERY_KEY, id],
    () => getInvoiceCarrier(id),
    {
      enabled: !!id,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useUpdateInvoiceCarrier() {
  const intl = useIntl().formatMessage;
  const query = useQueryClient();

  const mutation = useMutation(
    (obj: InvoiceCarrierUpdateRequest) => updateInvoiceCarrier(obj),
    {
      onSuccess() {
        query.invalidateQueries(['invoiceCarrier']);
        notification.success({
          message: intl({
            id: 'invoiceTransport.notification.update.success.title',
          }),
          description: intl({
            id: 'invoiceTransport.notification.update.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);
  return mutation;
}

export function useRemoveInvoiceCarrier() {
  const intl = useIntl();
  const query = useQueryClient();

  const mutation = useMutation((id: number) => removeInvoiceCarrier(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'invoiceTransport.notification.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'invoiceTransport.notification.remove.success.description',
        }),
      });
      query.invalidateQueries(['invoiceCarrier']);
    },
  });

  useServiceError(mutation.error);
  return mutation;
}
